import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import type { Order } from '@nucleus-commerce/api-client/admin';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@nucleus-commerce/ui/components/card';

import { useStore } from '@/shared/store/store-context';

/** Who ordered; a registered shopper links to their customer page. */
export function OrderCustomerCard({ order }: { order: Order }) {
  const { t } = useTranslation('orders');
  const store = useStore();
  const { can } = usePermissions();
  return (
    <Card className="gap-3">
      <CardHeader className="flex flex-row items-start justify-between gap-2">
        <CardTitle>{t('customer.title')}</CardTitle>
        <Badge variant="outline" className="text-muted-foreground">
          {order.customerId ? t('customer.registered') : t('customer.guest')}
        </Badge>
      </CardHeader>
      <CardContent>
        <dl className="flex flex-col gap-2 text-sm">
          <div>
            <dt className="text-muted-foreground text-xs">{t('customer.email')}</dt>
            <dd>
              {order.email ? (
                <a href={`mailto:${order.email}`} className="break-all hover:underline">
                  {order.email}
                </a>
              ) : (
                <span className="text-muted-foreground">{t('customer.noEmail')}</span>
              )}
            </dd>
          </div>
          <div>
            <dt className="text-muted-foreground text-xs">{t('customer.phone')}</dt>
            <dd>
              {order.phone ?? (
                <span className="text-muted-foreground">{t('customer.noPhone')}</span>
              )}
            </dd>
          </div>
          {order.customerId ? (
            <div>
              <dt className="text-muted-foreground text-xs">{t('customer.customerId')}</dt>
              <dd>
                {can('customers.read') ? (
                  <Link
                    to="/s/$storeId/customers/$customerId"
                    params={{ storeId: store.id, customerId: order.customerId }}
                    className="hover:underline"
                  >
                    {t('customer.viewProfile')}
                  </Link>
                ) : (
                  <span className="font-mono text-xs">{order.customerId}</span>
                )}
              </dd>
            </div>
          ) : null}
        </dl>
      </CardContent>
    </Card>
  );
}
